import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";

import { useCurrentCharacterName } from "../hooks/useCurrentCharacterName";
import {
  api,
  type NewsJobMode,
  type NewsReadingProfile,
  type NewsReportSubscriptionFrequency,
} from "../lib/api";
import { getCharacterShortName } from "../lib/characterName";
import { resolveNewsGoogleReconsentCompleteUrl } from "../lib/config";
import { logError, toUserMessage } from "../lib/errors";
import { GOOGLE_SCOPE_BUNDLES } from "../lib/googleScopes";
import { normalizeNewsGoogleAuthorizationUrl, writePendingNewsGoogleReconsentSnapshot } from "../lib/newsGoogleReconsent";
import {
  NEWS_INFORMATION_CATEGORY_GROUPS,
  buildNewsInformationKeyword,
  buildNewsInformationTitle,
  findNewsInformationCategory,
} from "../lib/newsInformationCategories";
import {
  createPendingNewsJobSnapshot,
  type PendingNewsJobSnapshot,
  writePendingNewsJobSnapshot,
} from "../lib/newsJob";
import { resolveNewsJobConflictDisplay } from "../lib/newsJobError";
import {
  readLineLinkSnapshot,
  resolveLineLinkSnapshotFromAccountState,
  type LineLinkSnapshot,
  writeLineLinkSnapshot,
} from "../lib/lineLink";
import {
  createNewsReportScheduleConfig,
  MONTHLY_DAY_OPTIONS,
  NEWS_REPORT_TIMEZONE,
  resolveNewsReportDeliveryFormat,
  WEEKDAY_OPTIONS,
} from "../lib/newsSubscriptions";
import { storage } from "../lib/storage";
import { UI_TEXT_TONE } from "../lib/uiTone";

type RequestSubmissionType = "single" | "subscription";
type MonthlyMode = "day_of_month" | "last_day";

type RequestRouteState = {
  returnTo?: string;
  requestType?: RequestSubmissionType;
  requestForm?: Partial<RequestPageFormState>;
};

type RequestPageFormState = {
  selectedCategoryId: string;
  keyword: string;
  notificationType: "line" | "email";
  email: string;
  readingProfile: NewsReadingProfile;
  includeAudio: boolean;
  subscriptionTitle: string;
  frequency: NewsReportSubscriptionFrequency;
  weeklyDayOfWeek: number;
  monthlyMode: MonthlyMode;
  monthlyDayOfMonth: number;
  deliveryTime: string;
};

type InlineError = {
  message: string;
  detail?: string;
  newsGoogleReconsent?: {
    authorizationUrl: string;
    requiredScopeBundle: string;
  };
};

const INITIAL_FORM: RequestPageFormState = {
  selectedCategoryId: "",
  keyword: "",
  notificationType: "line",
  email: "",
  readingProfile: "think",
  includeAudio: false,
  subscriptionTitle: "",
  frequency: "daily",
  weeklyDayOfWeek: 1,
  monthlyMode: "day_of_month",
  monthlyDayOfMonth: 1,
  deliveryTime: "08:00",
};

const REPORT_THEME_EXAMPLES = ["AIエージェント最新動向", "採用市場の変化", "業務効率化の事例"];
const NEWS_KEYWORD_DEFAULT_PLACEHOLDER = "例：大谷翔平、料金改定、神田の新店";
const NEWS_KEYWORD_DEFAULT_HELPER = "指定があれば優先します。少ない場合はテーマ全体からお届けします。";
const NEWS_KEYWORD_NO_CATEGORY_HELPER =
  "カテゴリを選ばず、この欄だけで気になるテーマを依頼することもできます。";

type NewsJobRequestPageProps = {
  mode: NewsJobMode;
};

export default function NewsJobRequestPage({ mode }: NewsJobRequestPageProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const characterName = useCurrentCharacterName();
  const routeState = (typeof location.state === "object" && location.state ? location.state : {}) as RequestRouteState;
  const returnTo = String(routeState.returnTo || "/request");

  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [lineLinkSnapshot, setLineLinkSnapshot] = useState<LineLinkSnapshot>(readLineLinkSnapshot());
  const [requestType, setRequestType] = useState<RequestSubmissionType>(
    routeState.requestType === "subscription" ? "subscription" : "single"
  );
  const [form, setForm] = useState<RequestPageFormState>({
    ...INITIAL_FORM,
    email: storage.getNewsEmail() ?? "",
  });
  const [error, setError] = useState<InlineError | null>(null);

  const canUseLine = lineLinkSnapshot.status === "linked";
  const isNewsMode = mode === "news";
  const selectedCategory = findNewsInformationCategory(form.selectedCategoryId);
  const resolvedKeyword = isNewsMode
    ? buildNewsInformationKeyword(form.selectedCategoryId, form.keyword)
    : form.keyword.trim();
  const defaultSubscriptionTitle = isNewsMode ? buildNewsInformationTitle(form.selectedCategoryId) : "";
  const includeAudioForPayload = mode === "report" && form.includeAudio;
  const title = mode === "report" ? "レポートを送ってもらう" : "情報収集メニューを作る";
  const subtitle =
    mode === "report"
      ? "調べたいテーマを入れると、考える材料になるレポートをまとめます。"
      : "気になるテーマを定期チェックして、仕事の情報収集や雑談の話題づくりに使えるニュースとして届けます。";
  const keywordLabel = mode === "report" ? "調査テーマ" : "特に見たい話題（任意）";
  const keywordPlaceholder =
    mode === "report"
      ? "例: 生成AIの最新動向と市場展望"
      : selectedCategory?.placeholder ?? NEWS_KEYWORD_DEFAULT_PLACEHOLDER;
  const keywordHelperText = selectedCategory?.helperText ?? NEWS_KEYWORD_DEFAULT_HELPER;
  const themeExamples = REPORT_THEME_EXAMPLES;
  const requestLabel = `${getCharacterShortName(characterName)}に頼む`;

  useEffect(() => {
    const nextForm = routeState.requestForm;
    if (!nextForm) return;

    setForm((prev) => ({
      selectedCategoryId:
        typeof nextForm.selectedCategoryId === "string" ? nextForm.selectedCategoryId : prev.selectedCategoryId,
      keyword: typeof nextForm.keyword === "string" ? nextForm.keyword : prev.keyword,
      notificationType:
        nextForm.notificationType === "email" || nextForm.notificationType === "line"
          ? nextForm.notificationType
          : prev.notificationType,
      email: typeof nextForm.email === "string" ? nextForm.email : prev.email,
      readingProfile: "think",
      includeAudio: typeof nextForm.includeAudio === "boolean" ? nextForm.includeAudio : prev.includeAudio,
      subscriptionTitle:
        typeof nextForm.subscriptionTitle === "string" ? nextForm.subscriptionTitle : prev.subscriptionTitle,
      frequency:
        nextForm.frequency === "weekly" || nextForm.frequency === "monthly" || nextForm.frequency === "daily"
          ? nextForm.frequency
          : prev.frequency,
      weeklyDayOfWeek:
        typeof nextForm.weeklyDayOfWeek === "number" ? nextForm.weeklyDayOfWeek : prev.weeklyDayOfWeek,
      monthlyMode:
        nextForm.monthlyMode === "last_day" || nextForm.monthlyMode === "day_of_month"
          ? nextForm.monthlyMode
          : prev.monthlyMode,
      monthlyDayOfMonth:
        typeof nextForm.monthlyDayOfMonth === "number" ? nextForm.monthlyDayOfMonth : prev.monthlyDayOfMonth,
      deliveryTime: typeof nextForm.deliveryTime === "string" ? nextForm.deliveryTime : prev.deliveryTime,
    }));
  }, [routeState.requestForm]);

  useEffect(() => {
    let active = true;

    (async () => {
      setLoading(true);
      try {
        const [accountState, userData] = await Promise.all([
          api.getMe(),
          api.getUser(),
        ]);

        if (!active) return;

        const nextLineSnapshot = resolveLineLinkSnapshotFromAccountState(accountState, readLineLinkSnapshot());
        writeLineLinkSnapshot(nextLineSnapshot);
        setLineLinkSnapshot(nextLineSnapshot);
        setForm((prev) => ({
          ...prev,
          notificationType: nextLineSnapshot.status === "linked" ? prev.notificationType : "email",
          email: prev.email || userData.email || "",
        }));
      } catch (refreshError) {
        logError(refreshError);
        if (!active) return;
        setLineLinkSnapshot(readLineLinkSnapshot());
      } finally {
        if (active) {
          setLoading(false);
        }
      }
    })();

    return () => {
      active = false;
    };
  }, []);

  const requestState = useMemo(
    () => ({
      requestType,
      requestForm: form,
      returnTo: location.pathname,
    }),
    [form, location.pathname, requestType]
  );

  const closePage = () => {
    navigate(returnTo, { replace: true });
  };

  const openLineLink = () => {
    navigate("/line-link", {
      state: {
        returnTo: location.pathname,
        returnState: requestState,
      },
    });
  };

  const startNewsGoogleReconsent = () => {
    const reconsent = error?.newsGoogleReconsent;
    if (!reconsent) return;

    const keyword = resolvedKeyword;
    const email = form.email.trim();
    const wizmateUserId = storage.getAuth().userId?.trim() ?? "";
    const redirectUrl = resolveNewsGoogleReconsentCompleteUrl();
    const authorizationUrl = normalizeNewsGoogleAuthorizationUrl(reconsent.authorizationUrl);

    if (!keyword || !email || !wizmateUserId || !authorizationUrl) {
      setError({
        message: "Google連携の再開に必要な情報を確認できませんでした。もう一度お試しください。",
      });
      return;
    }

    writePendingNewsGoogleReconsentSnapshot({
      keyword,
      notificationType: "email",
      email,
      wizmateUserId,
      scopeBundle: reconsent.requiredScopeBundle ?? GOOGLE_SCOPE_BUNDLES.bundle,
      authorizationUrl,
      redirectUrl,
      createdAt: new Date().toISOString(),
      mode,
      readingProfile: mode === "news" ? "think" : undefined,
      deliveryOption: includeAudioForPayload ? "text_and_audio" : "text",
    });

    window.location.assign(authorizationUrl);
  };

  const createScheduleConfig = () => {
    if (form.frequency === "weekly") {
      return createNewsReportScheduleConfig({
        frequency: "weekly",
        weeklyDayOfWeek: form.weeklyDayOfWeek,
      });
    }

    if (form.frequency === "monthly") {
      return createNewsReportScheduleConfig({
        frequency: "monthly",
        monthly:
          form.monthlyMode === "last_day"
            ? { monthlyMode: "last_day" }
            : { monthlyMode: "day_of_month", monthlyDayOfMonth: form.monthlyDayOfMonth },
      });
    }

    return createNewsReportScheduleConfig({ frequency: "daily" });
  };

  const handleSubmit = async () => {
    if (submitting) return;

    const keyword = resolvedKeyword;
    if (!keyword) {
      setError({
        message: mode === "news" ? "情報収集テーマを選ぶか、見たいテーマを入力してください。" : `${keywordLabel}を入力してください。`,
      });
      return;
    }

    if (form.notificationType === "line" && !canUseLine) {
      setError({ message: "LINE連携を完了してからお試しください。" });
      return;
    }

    const email = form.email.trim();
    if (form.notificationType === "email" && !email) {
      setError({ message: "送信先メールアドレスを入力してください。" });
      return;
    }

    if (requestType === "subscription" && !/^\d{2}:\d{2}$/.test(form.deliveryTime)) {
      setError({ message: "配信時刻を入力してください。" });
      return;
    }

    setSubmitting(true);
    setError(null);

    try {
      const deliveryFormat = resolveNewsReportDeliveryFormat(form.notificationType, includeAudioForPayload);

      if (requestType === "subscription") {
        const subscriptionTitle = form.subscriptionTitle.trim() || defaultSubscriptionTitle;
        await api.createNewsReportSubscription({
          mode,
          ...(subscriptionTitle ? { title: subscriptionTitle } : {}),
          keyword,
          frequency: form.frequency,
          schedule_config: createScheduleConfig(),
          delivery_time: form.deliveryTime,
          timezone: NEWS_REPORT_TIMEZONE,
          notification_type: form.notificationType,
          ...(form.notificationType === "email" ? { email } : {}),
          delivery_format: deliveryFormat,
          reading_profile: form.readingProfile,
          created_by_channel: "web",
        });

        if (form.notificationType === "email" && email) {
          storage.setNewsEmail(email);
        }

        navigate("/request/schedules", {
          replace: true,
          state: {
            message: "定期配信を設定しました。次回配信予定を一覧で確認できます。",
          },
        });
        return;
      }

      const response = await api.createNewsJob({
        keyword,
        notificationType: form.notificationType,
        ...(form.notificationType === "email"
          ? {
              email,
              redirectUrl: resolveNewsGoogleReconsentCompleteUrl(),
            }
          : {}),
        mode,
        ...(mode === "news" ? { readingProfile: "think" } : {}),
        deliveryOption: deliveryFormat,
      });

      if (form.notificationType === "email" && email) {
        storage.setNewsEmail(email);
      }

      const pendingSnapshot: PendingNewsJobSnapshot = {
        ...createPendingNewsJobSnapshot(response),
        mode,
        readingProfile: mode === "news" ? "think" : null,
        deliveryOption: deliveryFormat,
      };
      writePendingNewsJobSnapshot(pendingSnapshot);
      navigate("/request", { replace: true });
    } catch (submitError) {
      logError(submitError);
      const conflictDisplay = resolveNewsJobConflictDisplay(submitError);
      if (
        requestType === "single" &&
        form.notificationType === "email" &&
        conflictDisplay?.shouldShowGoogleReconnect &&
        conflictDisplay.connectAuthorizationUrl
      ) {
        const authorizationUrl = normalizeNewsGoogleAuthorizationUrl(conflictDisplay.connectAuthorizationUrl);
        if (!authorizationUrl) {
          setError({ message: "Google連携URLを確認できませんでした。しばらくしてからお試しください。" });
        } else {
          setError({
            message: "Gmail 送信には Google 連携の再確認が必要です。",
            detail: conflictDisplay.detail,
            newsGoogleReconsent: {
              authorizationUrl,
              requiredScopeBundle: conflictDisplay.requiredScopeBundle ?? GOOGLE_SCOPE_BUNDLES.bundle,
            },
          });
        }
      } else {
        setError({
          message: conflictDisplay?.message ?? toUserMessage(submitError),
          detail: conflictDisplay?.detail,
        });
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main className="min-h-screen overflow-x-hidden bg-[radial-gradient(circle_at_top,_rgba(250,233,220,0.95),_rgba(255,249,245,1)_55%)] px-4 py-6 text-[#4A3E39] sm:px-6 sm:py-10">
      <div className="mx-auto w-full max-w-xl">
        <header className="mb-6 flex items-center justify-between">
          <button
            type="button"
            onClick={closePage}
            className="text-sm font-bold text-[#A67C52] hover:text-[#7C5A4E]"
          >
            ←戻る
          </button>
          <p className="text-sm font-bold tracking-tight text-[#7C5A4E]">
            {mode === "report" ? "レポート" : "ニュース"}
          </p>
          <Link to="/home" className="text-xs font-bold text-[#9C867D] hover:text-[#7C5A4E]">
            WizMate
          </Link>
        </header>

        <section className="w-full rounded-[2.4rem] border border-white/80 bg-white/90 p-4 shadow-[0_24px_60px_rgba(124,90,78,0.16)] backdrop-blur-sm sm:p-7">
          <div className="mb-5">
            <p className="text-[11px] font-black tracking-[0.16em] text-[#A67C52]">{requestLabel}</p>
            <h1 className={`text-2xl font-black tracking-tight ${UI_TEXT_TONE.title}`}>{title}</h1>
            <p className={`mt-2 text-sm leading-6 ${UI_TEXT_TONE.body}`}>{subtitle}</p>
          </div>

          <div className="mb-5 grid grid-cols-2 gap-2 rounded-[1.4rem] border border-[#EADBC8] bg-[#FFF8F4] p-1">
            {([
              ["single", "1回依頼"],
              ["subscription", "定期配信"],
            ] as const).map(([value, label]) => (
              <button
                key={value}
                type="button"
                aria-pressed={requestType === value}
                onClick={() => {
                  setRequestType(value);
                  setError(null);
                }}
                disabled={submitting}
                className={`min-h-[42px] rounded-[1.1rem] px-3 py-2 text-sm font-black transition ${
                  requestType === value
                    ? "bg-white text-[#7C5A4E] shadow-sm"
                    : "text-[#9C6E5A] hover:bg-white/70"
                } disabled:cursor-not-allowed disabled:opacity-60`}
              >
                {label}
              </button>
            ))}
          </div>

          {loading ? <p className="text-sm text-[#7B6861]">読み込み中です...</p> : null}

          {!loading ? (
            <>
              {!canUseLine ? (
                <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:justify-end">
                  <button
                    type="button"
                    onClick={openLineLink}
                    disabled={submitting}
                    className="inline-flex items-center justify-center rounded-full bg-[#5F8B57] px-4 py-2.5 text-sm font-black text-white transition hover:bg-[#4E7348] disabled:cursor-not-allowed disabled:opacity-60"
                  >
                    LINEと連携する
                  </button>
                </div>
              ) : null}

              <div className="rounded-[1.5rem] border border-[#E4D5C8] bg-white px-5 py-5">
                  {isNewsMode ? (
                    <>
                      <fieldset>
                        <legend className="text-sm font-bold text-[#5D4D46]">気になる情報テーマを選ぶ</legend>
                        <p className="mt-2 text-xs font-bold leading-5 text-[#8B746A]">
                          選んだテーマを中心に、関連する新着もまとめてお届けします。
                        </p>
                        <div className="mt-4 grid gap-4">
                          {NEWS_INFORMATION_CATEGORY_GROUPS.map((group) => (
                            <div key={group.title}>
                              <p className="mb-2 text-[11px] font-black tracking-[0.12em] text-[#A67C52]">
                                {group.title}
                              </p>
                              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                                {group.categories.map((category) => {
                                  const selected = form.selectedCategoryId === category.id;
                                  return (
                                    <button
                                      key={category.id}
                                      type="button"
                                      aria-pressed={selected}
                                      onClick={() => {
                                        setForm((prev) => ({
                                          ...prev,
                                          selectedCategoryId: selected ? "" : category.id,
                                        }));
                                        setError(null);
                                      }}
                                      disabled={submitting}
                                      className={`min-h-[44px] rounded-2xl border px-3 py-2 text-left text-xs font-black leading-5 transition ${
                                        selected
                                          ? "border-[#D1907B] bg-[#FFF3E9] text-[#6F4B3A] shadow-sm"
                                          : "border-[#EADBC8] bg-[#FFFCFA] text-[#5D4D46] hover:bg-[#FFF8F4]"
                                      } disabled:cursor-not-allowed disabled:opacity-60`}
                                    >
                                      {category.label}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      </fieldset>

                      <label className="mt-5 block">
                        <span className="mb-2 block text-sm font-bold text-[#5D4D46]">{keywordLabel}</span>
                        <input
                          type="text"
                          value={form.keyword}
                          onChange={(event) => {
                            setForm((prev) => ({ ...prev, keyword: event.target.value }));
                            setError(null);
                          }}
                          placeholder={keywordPlaceholder}
                          className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                          disabled={submitting}
                          maxLength={100}
                        />
                      </label>
                      <p className="mt-2 text-xs font-bold leading-5 text-[#8B746A]">
                        {selectedCategory
                          ? keywordHelperText
                          : NEWS_KEYWORD_NO_CATEGORY_HELPER}
                      </p>
                    </>
                  ) : (
                    <>
                      <label className="block">
                        <span className="mb-2 block text-sm font-bold text-[#5D4D46]">{keywordLabel}</span>
                        <input
                          type="text"
                          value={form.keyword}
                          onChange={(event) => {
                            setForm((prev) => ({ ...prev, keyword: event.target.value }));
                            setError(null);
                          }}
                          placeholder={keywordPlaceholder}
                          className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                          disabled={submitting}
                          maxLength={100}
                        />
                      </label>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {themeExamples.map((example) => (
                          <button
                            key={example}
                            type="button"
                            onClick={() => {
                              setForm((prev) => ({ ...prev, keyword: example }));
                              setError(null);
                            }}
                            disabled={submitting}
                            className="rounded-full border border-[#EADBC8] bg-[#FFF8F4] px-3 py-1.5 text-xs font-bold text-[#7C5A4E] transition hover:bg-white disabled:cursor-not-allowed disabled:opacity-60"
                          >
                            {example}
                          </button>
                        ))}
                      </div>
                    </>
                  )}

                {requestType === "subscription" ? (
                  <div className="mt-4 rounded-[1.3rem] border border-[#F0E2D8] bg-[#FFF9F5] p-4">
                    <label className="block">
                      <span className="mb-2 block text-sm font-bold text-[#5D4D46]">タイトル（任意）</span>
                      <input
                        type="text"
                        value={form.subscriptionTitle}
                        onChange={(event) => {
                          setForm((prev) => ({ ...prev, subscriptionTitle: event.target.value }));
                          setError(null);
                        }}
                        placeholder={
                          mode === "report"
                            ? "例: 業界レポート週報"
                            : defaultSubscriptionTitle || "例: AI・生成AIニュース"
                        }
                        className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                        disabled={submitting}
                        maxLength={80}
                      />
                    </label>

                    <fieldset className="mt-4">
                      <legend className="mb-2 block text-sm font-bold text-[#5D4D46]">頻度</legend>
                      <div className="grid grid-cols-3 gap-2">
                        {([
                          ["daily", "毎日"],
                          ["weekly", "毎週"],
                          ["monthly", "毎月"],
                        ] as const).map(([value, label]) => (
                          <label
                            key={value}
                            className={`flex min-h-[42px] items-center justify-center rounded-2xl border px-2 py-2 text-sm font-black transition ${
                              form.frequency === value
                                ? "border-[#D1907B] bg-white text-[#7C5A4E]"
                                : "border-[#EADBC8] bg-[#FFFCFA] text-[#6B5851]"
                            } ${submitting ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                          >
                            <input
                              type="radio"
                              name={`frequency-${mode}`}
                              value={value}
                              checked={form.frequency === value}
                              onChange={() => {
                                setForm((prev) => ({ ...prev, frequency: value }));
                                setError(null);
                              }}
                              disabled={submitting}
                              className="sr-only"
                            />
                            {label}
                          </label>
                        ))}
                      </div>
                    </fieldset>

                    {form.frequency === "weekly" ? (
                      <label className="mt-4 block">
                        <span className="mb-2 block text-sm font-bold text-[#5D4D46]">曜日</span>
                        <select
                          value={form.weeklyDayOfWeek}
                          onChange={(event) => {
                            setForm((prev) => ({ ...prev, weeklyDayOfWeek: Number(event.target.value) }));
                            setError(null);
                          }}
                          disabled={submitting}
                          className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm font-bold text-[#5D4D46] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                        >
                          {WEEKDAY_OPTIONS.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                      </label>
                    ) : null}

                    {form.frequency === "monthly" ? (
                      <label className="mt-4 block">
                        <span className="mb-2 block text-sm font-bold text-[#5D4D46]">月次</span>
                        <select
                          value={
                            form.monthlyMode === "last_day"
                              ? "last_day"
                              : String(form.monthlyDayOfMonth)
                          }
                          onChange={(event) => {
                            const value = event.target.value;
                            setForm((prev) =>
                              value === "last_day"
                                ? { ...prev, monthlyMode: "last_day" }
                                : {
                                    ...prev,
                                    monthlyMode: "day_of_month",
                                    monthlyDayOfMonth: Number(value),
                                  }
                            );
                            setError(null);
                          }}
                          disabled={submitting}
                          className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm font-bold text-[#5D4D46] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                        >
                          {MONTHLY_DAY_OPTIONS.map((day) => (
                            <option key={day} value={day}>
                              {day}日
                            </option>
                          ))}
                          <option value="last_day">月末</option>
                        </select>
                      </label>
                    ) : null}

                    <label className="mt-4 block">
                      <span className="mb-2 block text-sm font-bold text-[#5D4D46]">配信時刻</span>
                      <input
                        type="time"
                        value={form.deliveryTime}
                        onChange={(event) => {
                          setForm((prev) => ({ ...prev, deliveryTime: event.target.value }));
                          setError(null);
                        }}
                        disabled={submitting}
                        className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm font-bold text-[#5D4D46] shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                      />
                    </label>
                  </div>
                ) : null}

                <fieldset className="mt-4 block">
                  <legend className="mb-2 block text-sm font-bold text-[#5D4D46]">通知先</legend>
                  <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                    <label
                      className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm shadow-sm transition-colors ${
                        form.notificationType === "line"
                          ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                          : "border-[#EADBC8] bg-white text-[#5D4D46]"
                      } ${canUseLine && !submitting ? "cursor-pointer" : "cursor-not-allowed opacity-50"}`}
                    >
                      <input
                        type="radio"
                        name={`notificationType-${mode}`}
                        value="line"
                        checked={form.notificationType === "line"}
                        onChange={() => {
                          setForm((prev) => ({ ...prev, notificationType: "line", includeAudio: false }));
                          setError(null);
                        }}
                        disabled={submitting || !canUseLine}
                        className="h-4 w-4 accent-[#A67C52]"
                      />
                      <span className="font-bold">LINE</span>
                    </label>

                    <label
                      className={`flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm shadow-sm transition-colors ${
                        form.notificationType === "email"
                          ? "border-[#D1907B] bg-[#FFF7F1] text-[#7C5A4E]"
                          : "border-[#EADBC8] bg-white text-[#5D4D46]"
                      } ${submitting ? "cursor-not-allowed opacity-60" : "cursor-pointer"}`}
                    >
                      <input
                        type="radio"
                        name={`notificationType-${mode}`}
                        value="email"
                        checked={form.notificationType === "email"}
                        onChange={() => {
                          setForm((prev) => ({ ...prev, notificationType: "email" }));
                          setError(null);
                        }}
                        disabled={submitting}
                        className="h-4 w-4 accent-[#A67C52]"
                      />
                      <span className="font-bold">メール</span>
                    </label>
                  </div>
                </fieldset>

                {form.notificationType === "email" ? (
                  <label className="mt-4 block">
                    <span className="mb-2 block text-sm font-bold text-[#5D4D46]">送信先メールアドレス</span>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(event) => {
                        setForm((prev) => ({ ...prev, email: event.target.value }));
                        setError(null);
                      }}
                      placeholder="例: your@example.com"
                      className="w-full rounded-2xl border border-[#EADBC8] bg-white px-4 py-3 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-[#D1907B]/20"
                      disabled={submitting}
                      maxLength={200}
                    />
                  </label>
                ) : null}

                {mode === "report" ? (
                  <label
                    className={`mt-4 flex items-center gap-3 rounded-2xl border px-4 py-3 text-sm shadow-sm ${
                      form.notificationType === "line" ? "opacity-60" : ""
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={form.includeAudio}
                      onChange={(event) => {
                        setForm((prev) => ({ ...prev, includeAudio: event.target.checked }));
                        setError(null);
                      }}
                      disabled={submitting || form.notificationType === "line"}
                      className="h-4 w-4 accent-[#A67C52]"
                    />
                    <span className="font-bold text-[#5D4D46]">音声ファイルを付ける</span>
                  </label>
                ) : (
                  <p className="mt-4 rounded-2xl border border-[#EADBC8] bg-[#FFFCFA] px-4 py-3 text-xs font-bold leading-5 text-[#8B746A]">
                    ニュースはテキストでお届けします。音声ファイル付きメールはレポートで利用できます。
                  </p>
                )}
              </div>

              {error ? (
                <div className="mt-4 rounded-2xl border border-[#E8B2A8] bg-[#FFF3F1] px-4 py-3 text-sm text-[#A1493E]">
                  <p>{error.message}</p>
                  {error.detail ? <p className="mt-1 text-xs text-[#B86A5D]">{error.detail}</p> : null}
                  {error.newsGoogleReconsent ? (
                    <button
                      type="button"
                      onClick={startNewsGoogleReconsent}
                      disabled={submitting}
                      className="mt-3 inline-flex items-center justify-center rounded-full bg-[#B07A55] px-4 py-2 text-sm font-black text-white transition hover:bg-[#986648] disabled:cursor-not-allowed disabled:opacity-60"
                    >
                      連携をやり直す
                    </button>
                  ) : null}
                </div>
              ) : null}

            </>
          ) : null}

          <div className="mt-6">
            <button
              type="button"
              onClick={handleSubmit}
              disabled={loading || submitting}
              className="w-full rounded-[1.4rem] bg-[#5D4D46] px-4 py-3 text-sm font-black text-[#FDF8F5] shadow-sm transition-all hover:bg-[#4A3E39] active:scale-[0.99] disabled:cursor-not-allowed disabled:opacity-60"
            >
              {submitting
                ? requestType === "subscription"
                  ? "設定中..."
                  : "送信中..."
                : requestType === "subscription"
                ? "定期配信を設定する"
                : "依頼する"}
            </button>
          </div>
        </section>
      </div>
    </main>
  );
}
